"use client";

import { useEffect, useMemo } from "react";
import {
  HardDrive,
  ShieldCheck,
  TrendingUp,
  Clock,
  RefreshCw,
  Timer,
} from "lucide-react";
import KpiCard from "@/components/dashboard/KpiCard";
import StatusBadge from "@/components/dashboard/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTenantSummary } from "@/contexts/TenantSummaryContext";
import { TenantOrganizationCard } from "@/components/dashboard/TenantOrganizationCard";

function formatBytesToTb(bytes: number): string {
  const tb = bytes / (1024 ** 4);
  return tb >= 1 ? tb.toFixed(1) : (bytes / (1024 ** 3)).toFixed(2);
}

function formatMsToMinutes(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const min = Math.floor(diffMs / 60_000);
  const h = Math.floor(min / 60);
  const d_ = Math.floor(h / 24);
  if (d_ > 0) return `${d_}d ago`;
  if (h > 0) return `${h}h ago`;
  if (min > 0) return `${min}m ago`;
  return "Just now";
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = bytes / 1024 ** 2;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return kb >= 1 ? `${kb.toFixed(1)} KB` : `${bytes} B`;
}

function formatDateTime(isoStr: string): string {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
}

/** Map API batch status to StatusBadge status */
function badgeStatus(apiStatus: string): "success" | "warning" | "error" | "pending" | "running" {
  if (apiStatus === "completed") return "success";
  if (apiStatus === "failed") return "error";
  if (apiStatus === "running" || apiStatus === "pending") return apiStatus as "running" | "pending";
  return "pending";
}

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary, loading, error: summaryError, refetch } = useTenantSummary();

  useEffect(() => {
    if (summaryError === "NOT_FOUND") {
      navigate("/enrollment", { replace: true });
    }
  }, [summaryError, navigate]);

  const error = summaryError && summaryError !== "NOT_FOUND" ? summaryError : null;

  const kpis = useMemo(() => {
    const storageTb = summary
      ? formatBytesToTb(summary.storageUsed)
      : "—";
    const quotaTb =
      summary?.storageLimit != null
        ? Number.isInteger(summary.storageLimit)
          ? String(summary.storageLimit)
          : summary.storageLimit.toFixed(1)
        : "—";
    const storageSub = t("dashboard.ofAllocated", { limit: quotaTb });
    const objects = summary?.objectsProtected != null ? summary.objectsProtected.toLocaleString() : "—";
    const successPct = summary != null ? `${(summary.successProportion * 100).toFixed(1)}%` : "—";
    const lastSync = summary?.lastSyncDate ? formatTimeAgo(summary.lastSyncDate) : "—";
    const avgDur = summary?.avgBatchDurationMs != null ? formatMsToMinutes(summary.avgBatchDurationMs) : "—";
    const syncsMonthValue = summary?.syncsThisMonth != null ? String(summary.syncsThisMonth) : (loading ? "…" : "—");

    return [
      { title: t("dashboard.totalStorage"), value: summary ? `${storageTb} TB` : (loading ? "…" : "—"), icon: HardDrive, subtitle: storageSub, trend: undefined as { value: string; positive: boolean } | undefined },
      { title: t("dashboard.objectsProtected"), value: objects, icon: ShieldCheck, subtitle: t("dashboard.acrossServices") },
      { title: t("dashboard.syncSuccess"), value: successPct, icon: TrendingUp, trend: undefined as { value: string; positive: boolean } | undefined },
      { title: t("dashboard.lastSync"), value: lastSync, icon: Clock, subtitle: t("dashboard.deltaSyncLabel") },
      { title: t("dashboard.syncsMonth"), value: syncsMonthValue, icon: RefreshCw },
      { title: t("dashboard.avgDuration"), value: avgDur, icon: Timer, trend: undefined as { value: string; positive: boolean } | undefined },
    ];
  }, [summary, loading, t]);

  const syncDurationChartData = useMemo(() => {
    const raw = summary?.last5SyncDurations ?? [];
    const reversed = [...raw].reverse();
    return reversed.map((ms, i) => ({
      day: `${i + 1}`,
      duration: ms != null ? Math.round(ms / 60_000) : 0,
    }));
  }, [summary?.last5SyncDurations]);

  const objectsByService = summary?.objectsByService ?? [];
  const storageDistribution = summary?.storageDistribution ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{String(t("dashboard.title"))}</h1>
        <p className="text-sm text-muted-foreground mt-1">{String(t("dashboard.subtitle"))}</p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !summary && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {summary && !error && (
        <TenantOrganizationCard
          summary={summary}
          loading={loading}
          onSaved={refetch}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
          <h3 className="text-sm font-medium text-foreground mb-4">{String(t("dashboard.objectsByService"))}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={objectsByService}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 91%)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(210, 100%, 19%)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
          <h3 className="text-sm font-medium text-foreground mb-4">{String(t("dashboard.syncDurationTrend"))}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={syncDurationChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 91%)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(215, 16%, 47%)" }} unit="m" />
              <Tooltip />
              <Line type="monotone" dataKey="duration" stroke="hsl(210, 100%, 19%)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
          <h3 className="text-sm font-medium text-foreground mb-4">{String(t("dashboard.storageDistribution"))}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={storageDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} strokeWidth={1}>
                {storageDistribution.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-lg border border-border/50 bg-card shadow-sm">
        <div className="p-5 border-b border-border/50">
          <h3 className="text-sm font-medium text-foreground">{String(t("dashboard.recentActivity"))}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">{String(t("dashboard.syncType"))}</TableHead>
              <TableHead className="text-xs">{String(t("dashboard.services"))}</TableHead>
              <TableHead className="text-xs">{String(t("dashboard.status"))}</TableHead>
              <TableHead className="text-xs">{String(t("dashboard.duration"))}</TableHead>
              <TableHead className="text-xs">{String(t("dashboard.size"))}</TableHead>
              <TableHead className="text-xs">{String(t("dashboard.started"))}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(summary?.recentActivity ?? []).map((row, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm font-medium">{row.type}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.services}</TableCell>
                <TableCell><StatusBadge status={badgeStatus(row.status)} /></TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {row.durationMs != null ? formatMsToMinutes(row.durationMs) : "—"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatBytes(row.totalBytes)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatDateTime(row.startedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Dashboard;
